import React from 'react';
import styles from './zoocasaFooterStyle.module.scss';
import TwitterIcon from 'components/icon/twitter-icon';
import FacebookIcon from 'components/icon/facebook-icon';
import InstagramIcon from 'components/icon/instagram-icon';
import LinkedInIcon from 'components/icon/linkedin-icon';
import MailIcon from 'components/icon/mail-icon';
import PhoneIcon from 'components/icon/phone-icon';
import FeedbackButton from 'components/feedback-button/feedback-button';
import Button, { SECONDARY_THEME } from 'components/control/button';
import ZoocasaUsaIcon from 'components/icon/zoocasa-usa-icon';
import { Route } from 'components/dynamic-page/route-matchers/types';
import Link from 'components/link';
import { buildClassName } from 'utils/build-class-name';
import { openEqualWebMenu } from 'utils/accessibility';
import { FooterConfig, FooterImageData, OrganizationConfig } from '@tenants/types/tenant';
import { FeatureFlags } from '../feature-flags/feature-flags';
import { CountryCode } from 'types/countries';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { Namespaces } from 'types/locales';

import type { FeaturesType } from 'contexts/features';

type ZoocasaFooterProps = {
  /** Determines if the accessibility widget should be shown */
  canShowAccessibilityWidget: boolean;
  features?: FeaturesType;
  footerConfig: FooterConfig;
  organizationConfig: OrganizationConfig;
  route: Route;
  countryCode: CountryCode;
} & ZoocasaFooterDebugProps;

type ZoocasaFooterDebugProps = {
  features?: FeaturesType;
};

export default function ZoocasaFooter({ canShowAccessibilityWidget, features, footerConfig, organizationConfig, route, countryCode }: ZoocasaFooterProps) {
  const address = organizationConfig.address({ siteLocation: countryCode });
  const phoneNumber = organizationConfig.phoneNumber(route);
  const emailAddress = organizationConfig.email(route);

  const brokerageLegalName = 'Zoocasa Realty Inc., Brokerage';

  const expLogo = <div className={styles['exp-logo-container']}>
    <div className={styles['owned-by-row']}>
      <p>Owned by</p>
      <div className={styles['exp-logo']}>
        <a href="https://exprealty.com/" target="_blank" rel="noreferrer">
          <Image src="/next/assets/images/exp-realty-logo.svg" alt="eXp Realty" fill unoptimized/>
        </a>
      </div>
    </div>
    <p className={styles['brokerage-legal-name']}>{brokerageLegalName}</p>
  </div>;

  return (
    <div className={styles.component} data-testid="zoocasa-footer">
      <div className={styles['footer-top']}>
        <div className={styles['footer-top-left']}>
          <div className={styles['footer-links']}>
            <div className={buildClassName(styles.block, styles.block1)}>
              <h3 className={styles.heading}>Company</h3>
              <TermsAndConditionsAndContactLinks canShowAccessibilityWidget={canShowAccessibilityWidget} />
            </div>
            <div className={buildClassName(styles.block, styles.block2)}>
              <h3 className={styles.heading}>Stay Connected</h3>
              <EmailPhoneNumberContacts phoneNumber={phoneNumber} emailAddress={emailAddress} />
              <p className={styles['brokerage-legal-name']}>{brokerageLegalName}</p>
              <p className={styles['office-address']}>
                {address}
              </p>
              <div className={styles.socials}>
                <a href={organizationConfig.twitterUrl} target="_blank" rel="noreferrer"><TwitterIcon /></a>
                <a href={organizationConfig.facebookUrl} target="_blank" rel="noreferrer"><FacebookIcon className={styles.invert} /></a>
                <a href={organizationConfig.instagramUrl} target="_blank" rel="noreferrer"><InstagramIcon className={styles.invert} /></a>
                <a href={organizationConfig.linkedinUrl} target="_blank" rel="noreferrer"><LinkedInIcon className={styles.invert} /></a>
              </div>
            </div>
            <div className={buildClassName(styles.block, styles.block3)}>
              <h3 className={styles.heading}>Download Our App</h3>
              <AppStoreLinks />
            </div>
            <div className={buildClassName(styles.block, styles.block4)}>
              <FeedbackButton className={styles['feedback-button']}/>
              <div className={styles['hide-mobile']}>{expLogo}</div>
              <div className={styles['hide-mobile']}>
                <ExpDynamicCopyright/>
              </div>
              <div className={styles['hide-desktop']}>{expLogo}</div>
              <div className={styles['hide-desktop']}><ExpDynamicCopyright/></div>
            </div>
          </div>
          <div className={styles['footer-big-text']}>
            <div className={styles['zoocasa-logo']}>
              <ZoocasaUsaIcon className={styles['zoocasa-by-exp-logo']} />
              <p className={styles['brokerage-legal-name']}>{brokerageLegalName}</p>
            </div>
            <div className={buildClassName(styles['join-section-mobile-container'])}>
              <JoinSection joinUsLink={footerConfig.footerColumns?.joinUsLink} footerImageData={footerConfig.footerColumns?.footerImageData} />
            </div>
          </div>
        </div>
        <div className={buildClassName(styles['footer-top-right'])}>
          <JoinSection joinUsLink={footerConfig.footerColumns?.joinUsLink} footerImageData={footerConfig.footerColumns?.footerImageData}/>
        </div>
      </div>
      <div className={buildClassName(styles['footer-bottom'])}>
        <div className={styles.wrap}><p>The trademarks MLS®, Multiple Listing Service® and the associated logos are owned by The Canadian Real Estate Association (CREA) and identify the quality of services provided by real estate professionals who are members of CREA.</p></div>
        <p>eXp Realty holds real estate brokerage licenses in multiple states. Zoocasa (Canada), Inc. holds real estate brokerage licenses in multiple provinces. For information on licenses please contact us at info@zoocasa.com</p>
        <p>For listings in Canada, the trademarks REALTOR®, REALTORS®, and the REALTOR® logo are controlled by The Canadian Real Estate Association (CREA) and identify real estate professionals who are members of CREA. The trademarks MLS®, Multiple Listing Service® and the associated logos are owned by CREA and identify the quality of services provided by real estate professionals who are members of CREA. Used under license.</p>
        <p>MLS® Compliance Statements<br/>
          Tony King &ndash; Designated Managing Broker, Broker Operations &ndash; TX.&nbsp;<br/>
          <Link target="_blank" href="https://drive.google.com/file/d/1oAApwK-x8v9_aWc_h9eEd9jQWjlZJs99/view?pli=1">Texas Real Estate Commission information about brokerage services</Link><br/>
          <Link target="_blank" href="https://www.trec.texas.gov/sites/default/files/pdf-forms/CN%201-5_0.pdf">Texas Real Estate Commission Consumer Protection Notice</Link>
        </p>
        <p>
          eXp Realty® is committed to adhering to the guidelines of The New York State Fair Housing Regulations.&nbsp;
          <Link target="_blank" href="https://dos.ny.gov/fair-housing">The Fair Housing Notice</Link> &rarr;&nbsp;
        </p>
        <p>
          *Standardized Operating Procedure for Purchasers of Real Estate Pursuant to Real Property Law 442-H.&nbsp;
          <Link target="_blank" href="https://drive.google.com/file/d/1o1iG6CO5hFgawNGJibmd34loBFJeIqqJ/view">Learn More</Link> &rarr;&nbsp;
        </p>
        <p>
          eXp Realty of California, Inc. | CA DRE# 01878277 | Deborah Penny &ndash; Designated Broker<br/>
          eXp Realty of Southern California, Inc. | CA DRE# 02187306 | Gina Saad &ndash; Designated Broker<br/>
          eXp Realty of Greater Los Angeles, Inc. | CA DRE# 02188471 | Anita Cruz &ndash; Designated Broker<br/>
          eXp Realty of Northern California, Inc. | CA DRE# 01951343 | Ryan Rosas Penny &ndash; Designated Broker<br/>
          <Link href="https://exprealty.com/fair-housing-and-reasonable-accommodations/">Fair Housing and Reasonable Accommodations</Link><br/>
          <Link href="https://exprealty.com/dmca/" >DMCA Notice</Link><br/>
        </p>
        <p>
          <Link href="https://drive.google.com/file/d/10uokR1nubSaGfyudkFLKC6JhYs1BnjGf/view">2026 AODA Compliance Report</Link><br/>
          Feedback can be submitted through our standard communication channels such as our internal chat platform, email, phone etc., and <u>accommodations are available</u> upon request. Zoocasa reviews all feedback received, acknowledges concerns, and works to respond in a timely manner. Feedback is assessed and addressed appropriately to support continuous improvement of our services and accessibility practices.
        </p>
      </div>
      <FeatureFlags features={features} />
    </div>
  );
}

const ExpDynamicCopyright = () => <div className={styles['exp-copyright']}><p> © {new Date().getFullYear()} eXp Realty. eXp World Holdings, Inc. All Rights Reserved</p></div>;

const EmailPhoneNumberContacts = ({ phoneNumber, emailAddress }: { phoneNumber: string; emailAddress: string }) => {
  return (
    <section className={styles.contact}>
      {phoneNumber && (
        <div className={styles['contact-item']}>
          <a href={`tel:${phoneNumber}`}>
            <PhoneIcon />
            <p>
              {phoneNumber}
            </p>
          </a>
        </div>
      )}
      {emailAddress && (
        <div className={styles['contact-item']}>
          <a href={`mailto:${emailAddress}`}>
            <MailIcon />
            <p>
              {emailAddress}
            </p>
          </a>
        </div>
      )}
    </section>
  );
};

const TermsAndConditionsAndContactLinks = ({ canShowAccessibilityWidget }) => {
  return (
    <div className={styles['terms-and-conditions']}>
      <div>
        <Link href="https://expworldholdings.com/terms-of-service/" target="_blank">Terms of Use</Link>
        <Link href="https://expworldholdings.com/privacy-policy/" target="_blank">Privacy Policy</Link>
        <a href="https://careers.zoocasa.com">Careers</a>
        <Link href="/about-us">About Us</Link>
        <Link href="/sitemap">Sitemap</Link>
        {canShowAccessibilityWidget && <a onClick={openEqualWebMenu}>Accessibility</a>}
      </div>
    </div>
  );
};

const AppStoreLinks = () => {
  return (
    <>
      <div className={styles['app-store-links']}>
        <a href="http://itunes.apple.com/WebObjects/MZStore.woa/wa/viewSoftware?id=301743811&mt=8" target="_blank" rel="noreferrer">
          <Image src="/next/assets/images/appleappstore.svg" alt="Apple Store" fill unoptimized/>
        </a>
        <a href="https://play.google.com/store/apps/details?id=com.zoocasareactnative" target="_blank" rel="noreferrer">
          <Image src="/next/assets/images/googleplay.svg" alt="Google Play Store" fill unoptimized/>
        </a>
      </div>
      <LinksToUSAndCAListingPages />
    </>
  );
};

const JoinSection = ({ joinUsLink, footerImageData }: { joinUsLink: string; footerImageData: FooterImageData }) => {
  const { t } = useTranslation(Namespaces.COMMON);
  
  return (
    <div className={styles['join-section']}>
      <div className={styles['join-image-container']}>
        <Image src={footerImageData.src} alt={t(footerImageData.alt.key, { defaultValue: footerImageData.alt.defaultValue })} fill />
      </div>
      <Link href={joinUsLink} target='_blank' className={styles['join-link']}>
        <Button theme={SECONDARY_THEME} label={'Find Your Home'} className={styles['join-button']} />
      </Link>
    </div>
  );
};

const LinksToUSAndCAListingPages = () => {
  return (
    <div className={styles['listing-links']}>
      <Link href="https://www.zoocasa.com/us-real-estate">US Real Estate Listings</Link>
      <Link href="https://www.zoocasa.com/ca-real-estate">Canada Real Estate Listings</Link>
    </div>
  );
};
